import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Package, Image, MessageSquare, HelpCircle, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { adminApi } from '../../lib/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    quotes: 0,
    pendingQuotes: 0,
    packages: 0,
    services: 0,
    gallery: 0,
    testimonials: 0,
    faqs: 0,
  });
  const [recentQuotes, setRecentQuotes] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [quotes, packages, services, gallery, testimonials, faqs] = await Promise.all([
          adminApi.getQuoteRequests(),
          adminApi.packages.list(),
          adminApi.services.list(),
          adminApi.gallery.list(),
          adminApi.testimonials.list(),
          adminApi.faqs.list(),
        ]);
        setStats({
          quotes: quotes.length,
          pendingQuotes: quotes.filter((q) => q.status === 'pending').length,
          packages: packages.length,
          services: services.length,
          gallery: gallery.length,
          testimonials: testimonials.length,
          faqs: faqs.length,
        });
        setRecentQuotes(quotes.slice(0, 5));
      } catch (error) {
        // Dashboard stats are best-effort; ignore load failures
      }
    })();
  }, []);

  const cards = [
    { label: 'Total Quote Requests', value: stats.quotes, icon: Mail, to: '/admin/quote-requests', highlight: stats.pendingQuotes > 0 ? `${stats.pendingQuotes} pending` : null },
    { label: 'Packages', value: stats.packages, icon: Package, to: '/admin/packages' },
    { label: 'Services', value: stats.services, icon: Briefcase, to: '/admin/services' },
    { label: 'Gallery Items', value: stats.gallery, icon: Image, to: '/admin/gallery' },
    { label: 'Testimonials', value: stats.testimonials, icon: MessageSquare, to: '/admin/testimonials' },
    { label: 'FAQs', value: stats.faqs, icon: HelpCircle, to: '/admin/faqs' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#EADBC8] mb-2" data-testid="admin-dashboard-title">Dashboard</h1>
        <p className="text-gray-400">Overview of your website content</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.label} to={c.to} data-testid={`stat-card-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Card className="bg-[#2A2420] border-[#C5A880]/20 hover:border-[#C5A880] transition-all duration-300 hover:scale-105">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">{c.label}</p>
                      <p className="text-3xl font-bold text-[#EADBC8]">{c.value}</p>
                      {c.highlight && (
                        <p className="text-[#C5A880] text-xs mt-2 font-semibold">{c.highlight}</p>
                      )}
                    </div>
                    <div className="w-12 h-12 bg-[#C5A880]/10 rounded-full flex items-center justify-center">
                      <Icon className="text-[#C5A880]" size={22} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#EADBC8]">Recent Quote Requests</h2>
          <Link to="/admin/quote-requests" className="text-[#C5A880] text-sm hover:underline">
            View all →
          </Link>
        </div>

        {recentQuotes.length === 0 ? (
          <Card className="bg-[#2A2420] border-[#C5A880]/20">
            <CardContent className="p-8 text-center text-gray-400">
              No quote requests yet
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {recentQuotes.map((q) => (
              <Card key={q.id} className="bg-[#2A2420] border-[#C5A880]/20">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-[#EADBC8]">{q.name}</p>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            q.status === 'pending'
                              ? 'bg-yellow-500/10 text-yellow-400'
                              : 'bg-green-500/10 text-green-400'
                          }`}
                        >
                          {q.status}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm truncate">
                        {q.eventType} · {q.eventDate} · {q.email}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
